/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from "react";
import Select, {
	GroupBase,
	SingleValue,
	StylesConfig
} from "react-select";
import { PortalShadeOptionItem } from "../../../Store/entities/assemblies/types";
import Filters, { filterConfig } from "./FabricSelectFilter";
import { useUser } from "../../../Store/user/hooks";
import { FabricViewModel } from "../../../powershadesApiTypes";

type OptionType = PortalShadeOptionItem<string>;

type FabricSelectProps = {
	value: string;
	fabrics: FabricViewModel[];
	options: PortalShadeOptionItem<void>[] | PortalShadeOptionItem<string>[] | undefined;
	onChange: (newFabricName: any) => void;
	disabled?: boolean;
	styles?: StylesConfig<OptionType, false, GroupBase<OptionType>>;
	isClearable?: boolean;
};

const FabricSelect = ({
	value,
	options,
	fabrics,
	disabled = false,
	onChange,
	styles = {},
	isClearable = false
}: FabricSelectProps) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [selectedFabric, setSelectedFabric] = useState<SingleValue<OptionType>>(value
		? { label: value, title: value, value }
		: null);
	const [selectedFilters, setSelectedFilters] = useState(filterConfig.reduce<Record<string, string | null>>(
		(acc, { label }) => {
			acc[label] = null;
			return acc;
		},
		{}
	));
	
	const { theme } = useUser();

	const handleChange = (e) => {
		setSelectedFabric(e);
		onChange(e.value);
	};

	const filterMapping = {
		Rail: (fabric: FabricViewModel) => {
			const sel = selectedFilters.Rail;
			return sel === null
				? true
				: fabric.is_railroadable === (sel === "Railroadable");
		},
		Seam: (fabric: FabricViewModel) => {
			const sel = selectedFilters.Seam;
			return sel === null
				? true
				: fabric.is_seamable === (sel === "Seamable");
		},
		Opacity: (fabric: FabricViewModel) => {
			const sel = selectedFilters.Opacity;
			return sel === null
				? true
				: fabric.opacity === sel;
		},
	};

	const filteredFabrics = useMemo(() => (
		fabrics.filter((fabric) => Object.values(filterMapping).every((fn) => fn(fabric)))
	), [fabrics, selectedFilters]);

	const fabricOptions = useMemo(() => {
		if (!options) return [];
		if (Object.keys(selectedFilters).length === 0) {
			return options as OptionType[];
		}
		const allowed = new Set(filteredFabrics.map((f) => f.name));
		return (options as OptionType[])
			.filter((opt, index) => allowed.has(opt.value)
				&& options.findIndex((o) => o.value === opt.value) === index);
	}, [options, filteredFabrics]);
	
	const baseStyles: StylesConfig<OptionType, false, GroupBase<OptionType>> = {
		...styles,
		container: (base) => ({
			...base,
			cursor: disabled ? "not-allowed" : undefined
		}),
		menuPortal: (base) => ({
			...base,
			zIndex: 9999,
		}),
		control: (base, props) => ({
			...base,
			borderRadius: props.menuIsOpen ? "0.375rem 0.375rem 0 0" : "0.375rem",
			borderColor: props.isFocused ? "#8dc63f" : "var(--bs-border-color)",
			boxShadow: props.isFocused ? "0 0 0 1px #8dc63f" : base.boxShadow,
			textAlign: "start",
			backgroundColor: ((theme === "dark" && !disabled) || (theme === "light" && disabled))
				? "var(--bs-secondary-bg)"
				: theme === "dark"
					? "#353638"
					: "var(--bs-body-bg)",
			opacity: disabled ? 0.80 : 1,
			"&:hover": {
				borderColor: props.isFocused ? "#8dc63f" : "var(--bs-border-color)",
				boxShadow: props.isFocused ? "0 0 0 1px #8dc63f" : "none",
			},
			cursor: disabled ? "not-allowed" : "pointer",
			pointerEvents: disabled ? "none" : "auto",
		}),
		input: (base) => ({
			...base,
			opacity: disabled ? 0 : 1,
			pointerEvents: disabled ? "none" : "auto",
		}),
		placeholder: (base) => ({
			...base,
			marginLeft: "6px",
		}),
		singleValue: (base) => ({
			...base,
			marginLeft: "6px",
			zIndex: 1,
			color: theme === "dark" && disabled ? "#D9D9D9" : "var(--bs-body-color)",
		}),
		option: (base, props) => ({
			...base,
			backgroundColor: props.isSelected
				? "#8dc63f"
				: props.isFocused
					? "var(--bs-tertiary-bg)"
					: undefined,
			cursor: "pointer",
			color: props.isSelected ? "white" : "var(--bs-body-color)",
			':active': {
				...base[':active'],
				backgroundColor: "#8dc63f",
				color: "white"
			}
		}),
		menu: (base) => ({
			...base,
			marginTop: "0px",
			textAlign: "start",
			border: "1px solid var(--bs-border-color)",
			borderEndStartRadius: "var(--bs-border-radius)",
			borderEndEndRadius: "var(--bs-border-radius)"
		}),
		dropdownIndicator: (base) => ({ ...base, color: "#a7a9ac" }),
		indicatorSeparator: () => ({}),
	} as StylesConfig<OptionType, false, GroupBase<OptionType>>;

	const mergedStyles = {
		...baseStyles,
		...Object.keys(styles).reduce((acc, key) => {
			acc[key] = (provided, state) => {
				const internal = baseStyles[key]?.(provided, state) || provided;
				const external = styles[key]?.(provided, state) || {};
				return {
					...internal,
					...external,
				};
			};
			return acc;
		}, {})
	};

	const filtersWrapper = (props) => <Filters
		{...props}
		selectedFilters={selectedFilters}
		setSelectedFilters={setSelectedFilters}
	/>;

	useEffect(() => {
		if(!menuOpen)
		{
			setSelectedFabric(value
		? { label: value, title: value, value }
		: null);
		}

	}, [value]);
	
	return (
		<Select<OptionType, false, GroupBase<OptionType>>
			options={fabricOptions}
			value={selectedFabric ?? null}
			onChange={handleChange}
			components={{ MenuList: filtersWrapper }}
			styles={mergedStyles}
			menuPortalTarget={document.body}
			isClearable={isClearable}
			placeholder={false}
			menuIsOpen={!disabled && menuOpen}
			onMenuOpen={() => !disabled && setMenuOpen(true)}
			onMenuClose={() => setMenuOpen(false)}
		/>
	);
};

export default FabricSelect;
